// Phase 6B — Minimal Markdown renderer (zero deps).
//
// Supports the subset called out in the directive: headings (# ## ###),
// bold (**), italic (*), unordered + ordered lists, blockquotes (>),
// links ([t](url)), inline code (`x`), code blocks (```), horizontal rule (---).
// Styling is intentionally on-brand (Lifted-green accents).

import React from "react";
import DOMPurify from "dompurify";

// Iter73 — defence-in-depth: sanitize the curated HTML output of the
// inline transforms below before it reaches dangerouslySetInnerHTML.
// We already escape() raw input first and only allow https? links, but
// DOMPurify removes any residual risk if an upstream transform is ever
// loosened.
const _SAFE_HTML_CONFIG = {
  ALLOWED_TAGS: ["strong", "em", "code", "a"],
  ALLOWED_ATTR: ["href", "target", "rel", "class"],
  ALLOWED_URI_REGEXP: /^(?:https?:\/\/|\.\/|\/)/i,
};
function safeHtml(html) {
  return DOMPurify.sanitize(html, _SAFE_HTML_CONFIG);
}

function escape(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function inline(s) {
  let out = escape(s);
  // bold + italic + inline code
  out = out.replace(/`([^`]+)`/g, '<code class="bg-black/40 text-lifted-green px-1 py-0.5 rounded text-xs font-mono">$1</code>');
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white">$1</strong>');
  out = out.replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>');
  out = out.replace(/\[([^\]]+)\]\((https?:[^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-lifted-green underline">$1</a>');
  return out;
}

function renderBlocks(md) {
  const lines = (md || "").split(/\r?\n/);
  const blocks = [];
  let buf = [];
  let inCode = false;
  let codeBuf = [];
  let listKind = null;  // "ul" | "ol" | null
  let listItems = [];

  const flushList = () => {
    if (!listKind) return;
    blocks.push({ kind: listKind, items: listItems });
    listKind = null; listItems = [];
  };
  const flushPara = () => {
    if (buf.length === 0) return;
    blocks.push({ kind: "p", text: buf.join(" ") });
    buf = [];
  };

  for (const raw of lines) {
    const ln = raw;
    if (inCode) {
      if (ln.trim().startsWith("```")) {
        blocks.push({ kind: "code", text: codeBuf.join("\n") });
        codeBuf = []; inCode = false;
      } else { codeBuf.push(ln); }
      continue;
    }
    if (ln.trim().startsWith("```")) { flushPara(); flushList(); inCode = true; continue; }
    if (ln.trim() === "") { flushPara(); flushList(); continue; }
    if (/^---+\s*$/.test(ln)) { flushPara(); flushList(); blocks.push({ kind: "hr" }); continue; }
    const h = ln.match(/^(#{1,3})\s+(.+)$/);
    if (h) { flushPara(); flushList(); blocks.push({ kind: "h", level: h[1].length, text: h[2] }); continue; }
    if (/^>\s+/.test(ln)) { flushPara(); flushList(); blocks.push({ kind: "blockquote", text: ln.replace(/^>\s+/, "") }); continue; }
    const ul = ln.match(/^[\-\*]\s+(.+)$/);
    if (ul) { flushPara(); if (listKind !== "ul") { flushList(); listKind = "ul"; } listItems.push(ul[1]); continue; }
    const ol = ln.match(/^\d+\.\s+(.+)$/);
    if (ol) { flushPara(); if (listKind !== "ol") { flushList(); listKind = "ol"; } listItems.push(ol[1]); continue; }
    flushList();
    buf.push(ln);
  }
  flushPara(); flushList();
  return blocks;
}

export default function Markdown({ source, className = "" }) {
  const blocks = renderBlocks(source);
  return (
    <div className={`space-y-3 text-sm leading-relaxed text-lifted-text ${className}`}>
      {blocks.map((b, i) => {
        if (b.kind === "h") {
          const cls = b.level === 1
            ? "font-anton text-3xl uppercase text-white"
            : b.level === 2
              ? "font-anton text-2xl uppercase text-white"
              : "font-bebas tracking-[0.22em] text-lifted-green text-sm";
          return <div key={i} className={cls} dangerouslySetInnerHTML={{ __html: safeHtml(inline(b.text)) }} />;
        }
        if (b.kind === "p") {
          return <p key={i} className="text-lifted-text" dangerouslySetInnerHTML={{ __html: safeHtml(inline(b.text)) }} />;
        }
        if (b.kind === "ul") {
          return (
            <ul key={i} className="list-disc pl-5 space-y-1 marker:text-lifted-green">
              {b.items.map((it, j) => <li key={j} dangerouslySetInnerHTML={{ __html: safeHtml(inline(it)) }} />)}
            </ul>
          );
        }
        if (b.kind === "ol") {
          return (
            <ol key={i} className="list-decimal pl-5 space-y-1 marker:text-lifted-green">
              {b.items.map((it, j) => <li key={j} dangerouslySetInnerHTML={{ __html: safeHtml(inline(it)) }} />)}
            </ol>
          );
        }
        if (b.kind === "blockquote") {
          return (
            <blockquote key={i} className="border-l-2 border-lifted-green/50 pl-3 italic text-lifted-dim"
              dangerouslySetInnerHTML={{ __html: safeHtml(inline(b.text)) }} />
          );
        }
        if (b.kind === "code") {
          return (
            <pre key={i} className="bg-black/60 border border-white/10 rounded-md p-3 text-xs font-mono text-lifted-green overflow-x-auto whitespace-pre">
              {b.text}
            </pre>
          );
        }
        if (b.kind === "hr") return <hr key={i} className="border-white/10" />;
        return null;
      })}
    </div>
  );
}
